import { Banner } from '@sqs/rosetta-compositions/banner/next';
import { Chip, Tabs, type TabsOptionProps } from '@sqs/rosetta-elements';
import { Search } from '@sqs/rosetta-icons';
import { Box, Button, Flex, Text } from '@sqs/rosetta-primitives';
import { useTheme } from '@sqs/rosetta-styled';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NAV_USERS, NAV_USERS_INVITED, NAV_USERS_PENDING_MIGRATION } from '../../const/navigation';
import { useI18n } from '../../i18n';

import { selectEnterprise } from '../../stores/currentUser';
import { selectAll as selectAllInvitedOrgUsers } from '../../stores/invitedOrgUsers';
import { RootState } from '../../stores/rootReducer';
import { useAppSelector } from '../../stores/store';
import { enterpriseHasSsoProvider, ssoProviderMap } from '../../utils/enterpriseUtils';
import { CreateUser } from '../CreateUser/CreateUser';
import { PageHeader } from '../PageHeader';
import { ActiveUsersListResults } from './ActiveUsersListResults';
import { InvitedUsersListResults } from './InvitedUsersListResults';
import { PendingMigrationUsersListResults } from './PendingMigrationUsersListResults';

interface UsersProps {
  readonly tab: typeof NAV_USERS | typeof NAV_USERS_INVITED | typeof NAV_USERS_PENDING_MIGRATION;
}

export const Users = ({ tab }: UsersProps) => {
  const { fontSizes, space } = useTheme();
  const { T, t } = useI18n();
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(false);
  const [showSsoBanner, setShowSsoBanner] = useState(true);

  const { expiredInvites, enterprise } = useAppSelector((state: RootState) => {
    return {
      enterprise: selectEnterprise(state.currentUser),
      expiredInvites: selectAllInvitedOrgUsers(state.invitedOrgUsers).filter((user) => user.status === 'EXPIRED').
      length
    };
  });

  const hasSsoProvider = enterpriseHasSsoProvider(enterprise);

  const mobileSearchButton = () => {
    return (
      <Button.Tertiary
        key="show-search"
        type="button"
        aria-label={t("Search", {}, { project: 'enterprise-dashboard' })}
        onClick={() => setShowSearch(!showSearch)}>

        <Search />
      </Button.Tertiary>);

  };

  const tabOptions: TabsOptionProps[] = [
  {
    value: NAV_USERS,
    label: t("Active", {}, { project: 'enterprise-dashboard' }),
    sx: {
      marginRight: space[5],
      fontSize: fontSizes[4]
    }
  }];


  if (!hasSsoProvider) {
    tabOptions.push({
      value: NAV_USERS_INVITED,
      label: (color) => {
        return (
          <Flex color={color}>
            <Text.Body as={'div'} m={0} color={color} fontWeight={500}>
              <T project="enterprise-dashboard">{"Invited"}</T>
            </Text.Body>
            {expiredInvites > 0 ?
            <Chip usage="badge" sx={{ marginLeft: '8px' }} status="error" label={expiredInvites} /> :

            <></>
            }
          </Flex>);

      },
      sx: {
        marginRight: space[5],
        fontSize: fontSizes[4]
      }
    });
  }

  if (enterprise?.isMigratedOrg) {
    tabOptions.push({
      value: NAV_USERS_PENDING_MIGRATION,
      label: t("Pending Migration", {}, { project: 'enterprise-dashboard' }),
      sx: {
        fontSize: fontSizes[4]
      }
    });
  }

  const getTableForTab = () => {
    switch (tab) {
      case NAV_USERS_PENDING_MIGRATION:
        return <PendingMigrationUsersListResults showSearch={showSearch} />;
      case NAV_USERS:
        return <ActiveUsersListResults showSearch={showSearch} />;
      case NAV_USERS_INVITED:
        return <InvitedUsersListResults showSearch={showSearch} />;
      default:
        return <></>;
    }
  };

  const changeTab = (v: string) => {
    navigate(v);
  };

  // show tabs for:
  // Non-SSO orgs
  // SSO orgs that have been migrated (to see pending migration users)

  // don't show tabs for:
  // SSO orgs that haven't been migrated (no need for "Active" and extra padding)
  const showTabs = !(hasSsoProvider && !enterprise?.isMigratedOrg);

  return (
    <Box position="relative">
      {hasSsoProvider && showSsoBanner &&
      <Banner mt={7}>
          <Banner.Main>
            <Banner.Row>
              <Banner.Glyph />
              <Banner.Column>
                <Banner.Title>
                  {t("Your organization is using {provider}",

                { provider: ssoProviderMap(enterprise?.loginMethod) }, {
                  project: 'enterprise-dashboard' })
                }
                </Banner.Title>
                <Banner.Body>
                  <T project="enterprise-dashboard">{"Users can only be invited through your current SSO provider."}</T>
                </Banner.Body>
              </Banner.Column>
            </Banner.Row>
            <Banner.Action onClick={() => setShowSsoBanner(false)}>
              <T project="enterprise-dashboard">{"Dismiss"}</T>
            </Banner.Action>
          </Banner.Main>
        </Banner>
      }
      <PageHeader
        actionButton={<CreateUser disabled={hasSsoProvider} />}
        mobileActions={[mobileSearchButton(), <CreateUser key="create-user-btn" disabled={hasSsoProvider} />]}
        title={t("Users", {}, { project: 'enterprise-dashboard' })} />

      {showTabs &&
      <Box mb={7}>
          <Tabs
          htmlAttributes={{ css: { '&:before': { height: 0 } } }}
          options={tabOptions}
          value={tab}
          onChange={changeTab} />

        </Box>
      }
      {getTableForTab()}
    </Box>);

};